import React from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";

import { Button } from "@/components/ui/button";
import SectionCard from "@/pages/appointmentDetails/SectionCard";
import { buildPathWithCurrentTenant } from "@/lib/tenantPath";

const formatDateOnly = (value) => {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
};

// Button styling to match Edit (pencil) button on Prospect Details card
const accentIconButtonClass =
  "h-9 w-9 rounded-full bg-indigo-500/20 p-0 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30";

export default function AppointmentLeadDetailsSection({ lead, fmtDateTime, clientCreateDate, appointmentCreateDate }) {
  const navigate = useNavigate();
  
  return (
    <SectionCard 
      title="Lead Details"
      right={
        lead?.id && (
          <Button
            type="button"
            variant="secondary"
            className={accentIconButtonClass}
            onClick={() => navigate(buildPathWithCurrentTenant(`/app/leads/${lead.id}`))}
            data-testid="appointment-lead-details-view-button"
          >
            <Eye className="h-4 w-4" />
          </Button>
        )
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="appointment-lead-details">
        <div className="space-y-2">
          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">Lead Source</div>
            <div className="text-slate-100" data-testid="appointment-lead-source">
              {lead.lead_source_name || lead.lead_source || "—"}
            </div>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">Campaign Name</div>
            <div className="text-slate-100" data-testid="appointment-lead-campaign">
              {lead.campaign_name || "—"}
            </div>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">GHL Contact ID</div>
            <div className="text-slate-100" data-testid="appointment-lead-id">
              {lead.lead_external_id || lead.ghl_contact_id || "—"}
            </div>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">MissionAssist ID</div>
            <div className="text-slate-100" data-testid="appointment-lead-missionassist-id">
              {lead.id || "—"}
            </div>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">Household ID</div>
            <div className="text-slate-100" data-testid="appointment-lead-household-id">
              {lead.household_id || "—"}
            </div>
          </div>
          {clientCreateDate ? (
            <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Client Create Date</div>
              <div className="text-slate-100" data-testid="appointment-client-create-date">
                {formatDateOnly(clientCreateDate)}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">Ad Set Name</div>
            <div className="text-slate-100" data-testid="appointment-lead-ad-set">
              {lead.ad_set_name || "—"}
            </div>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">Form Name</div>
            <div className="text-slate-100" data-testid="appointment-lead-form-name">
              {lead.form_name || "—"}
            </div>
          </div>
          {appointmentCreateDate ? (
            <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Appointment Create Date</div>
              <div className="text-slate-100" data-testid="appointment-create-date">
                {formatDateOnly(appointmentCreateDate)}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">Ad Name</div>
            <div className="text-slate-100" data-testid="appointment-lead-ad-name">
              {lead.ad_name || "—"}
            </div>
          </div>

          <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
            <div className="text-slate-200/70">Lead Create Date</div>
            <div className="text-slate-100" data-testid="appointment-lead-created-at">
              {lead.created_at ? formatDateOnly(lead.created_at) : "—"}
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
